/**
 * Serviço e hook para dados de admin (ex.: listagem de instituições).
 */

import { useEffect, useState, useMemo } from 'react'
import { api } from '@/lib/api/client'
import type {
  AdminCampaignsListResponse,
  AdminInstitutionsResponse,
} from '@/types/api'
import type { InstitutionGroup } from '@/components/InstitutionSelector'

const ADMINISTRACAO_GROUP: InstitutionGroup = {
  label: 'ADMINISTRAÇÃO',
  options: [
    {
      value: 'meu-escritorio',
      label: 'Administração',
      subtitle: 'Visão Geral',
      icon: 'mdi:briefcase-outline',
    },
  ],
}

export const DEFAULT_ADMIN_INSTITUTION_VALUE = 'meu-escritorio'

/**
 * Busca a lista paginada de instituições para o admin.
 * Requer token de admin (requireAuth: true).
 */
export async function fetchAdminInstitutions(
  options: { perPage?: number; search?: string } = {}
): Promise<AdminInstitutionsResponse> {
  const { perPage = 100, search } = options
  const params = new URLSearchParams()
  params.set('per_page', String(perPage))
  if (search !== undefined && search.trim() !== '') {
    params.set('search', search.trim())
  }
  const endpoint = `/api/admin/institutions?${params.toString()}`
  return api.get<AdminInstitutionsResponse>(endpoint, { requireAuth: true })
}

export interface FetchAdminCampaignsOptions {
  search?: string
  perPage?: number
  institutionId?: number
  page?: number
  signal?: AbortSignal
}

/**
 * Busca a lista paginada de campanhas para o admin.
 * Requer token de admin (requireAuth: true).
 */
export async function fetchAdminCampaigns(
  options: FetchAdminCampaignsOptions = {}
): Promise<AdminCampaignsListResponse> {
  const { search, perPage = 50, institutionId, page, signal } = options
  const params = new URLSearchParams()
  params.set('per_page', String(Math.min(100, Math.max(1, perPage))))
  if (search !== undefined && search.trim() !== '') {
    params.set('search', search.trim())
  }
  if (institutionId !== undefined && institutionId !== null) {
    params.set('institution_id', String(institutionId))
  }
  if (page !== undefined && page >= 1) {
    params.set('page', String(page))
  }
  const endpoint = `/api/admin/campaigns?${params.toString()}`
  return api.get<AdminCampaignsListResponse>(endpoint, { requireAuth: true, signal })
}

function buildGroupsFromResponse(institutions: AdminInstitutionsResponse['institutions']): InstitutionGroup[] {
  const clientesOptions = institutions.map((inst) => ({
    value: String(inst.id),
    label: inst.name,
    subtitle: inst.document ?? undefined,
    icon: 'mdi:office-building' as const,
  }))
  return [
    ADMINISTRACAO_GROUP,
    {
      label: 'CLIENTES',
      options: clientesOptions,
    },
  ]
}

export interface UseAdminInstitutionsResult {
  groups: InstitutionGroup[]
  loading: boolean
  error: Error | null
}

/**
 * Hook que carrega as instituições quando o usuário é admin.
 * Retorna groups no formato do InstitutionSelector (ADMINISTRAÇÃO + CLIENTES da API).
 */
export function useAdminInstitutions(isAdmin: boolean): UseAdminInstitutionsResult {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [institutions, setInstitutions] = useState<AdminInstitutionsResponse['institutions']>([])

  useEffect(() => {
    if (!isAdmin) {
      const sync = window.setTimeout(() => {
        setLoading(false)
        setInstitutions([])
        setError(null)
      }, 0)
      return () => window.clearTimeout(sync)
    }

    let cancelled = false
    const initSync = window.setTimeout(() => {
      setLoading(true)
      setError(null)
    }, 0)

    fetchAdminInstitutions({ perPage: 100 })
      .then((data) => {
        if (!cancelled) {
          setInstitutions(data.institutions)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setInstitutions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      window.clearTimeout(initSync)
    }
  }, [isAdmin])

  const groups = useMemo(() => buildGroupsFromResponse(institutions), [institutions])

  return { groups, loading, error }
}
